// SegmentPicker — reusable component that lets the user pick a segment
// type (letters/digits/year/month/serial) and configure it, including the
// per-segment "next separator" sub-option added in iter_144a.
//
// Extracted from SkuFamilyBuilderDialog.js in iter_145 to reduce that file
// below the 700-line guideline. No behaviour change — this is a pure move
// of the SegmentPicker function + its Input/Label/Divide imports.

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Divide } from '@phosphor-icons/react';
import { SEG_TYPES, previewSegment } from './sku-segment-helpers';

export default function SegmentPicker({ value, onChange, positionLabel: _positionLabel, showNextSep = true }) {
  const t = value?.type;
  const setType = (type) => {
    // Preserve label + description + nextSep so relabelling / adding a
    // trailing separator doesn't get lost when the user switches type.
    // iter_143k · description added per user request "still I cannot name
    // or explain what's the meaning or naming in the 分類 tab".
    // iter_144a · nextSep added per user "I use lettering ITM but when I
    // choose separator it disappeared" — trailing separator is now a
    // per-segment sub-option, not a segment-replacing type choice.
    // iter_144c · Label is NO LONGER pre-populated with positionLabel — per
    // user "please make the naming of 分類 mandatory" — the field is now
    // required and blank until the user types something meaningful.
    const defaults = { letters: { value: '' }, digits: { value: '' }, serial: { width: 5, start: 0 } };
    onChange({
      label: value?.label || '',
      description: value?.description || '',
      nextSep: value?.nextSep || '',
      type,
      ...(defaults[type] || {}),
    });
  };
  // iter_144c · Mandatory-label surface — show a red asterisk + error state
  // when the input is empty AND the segment already has a type picked (so we
  // don't nag the user before they've even started). Blank-label validation
  // is also enforced in the parent's `errors[]` list on the preview step and
  // in the Next button's disabled logic.
  const labelInvalid = t && !(value?.label || '').trim();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">
            この分類の名前 <span className="text-rose-500">*</span>
          </Label>
          <Input
            data-testid="seg-label-input"
            value={value?.label || ''}
            onChange={(e) => onChange({ ...value, label: e.target.value })}
            placeholder={`例: 部門コード / 材質 / 大分類名 など`}
            maxLength={20}
            aria-invalid={labelInvalid || undefined}
            className={`h-9 ${labelInvalid ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`}
          />
          {labelInvalid && (
            <div className="text-[11px] text-rose-600 mt-1" data-testid="seg-label-error">
              分類の名前は必須です（例: 部門コード / 材質 など）
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">意味・使い方の説明（任意）</Label>
          <Input
            data-testid="seg-description-input"
            value={value?.description || ''}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            placeholder="例: SD=ソルダリング, SB=センスビー"
            maxLength={80}
            className="h-9"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SEG_TYPES.map((opt) => {
          const Icon = opt.icon;
          const active = t === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              data-testid={`seg-type-${opt.key}`}
              onClick={() => setType(opt.key)}
              className={`text-left rounded-xl border-2 p-3 transition-all ${
                active
                  ? `bg-gradient-to-br ${opt.tone} shadow-sm ring-2 ring-offset-1 ring-current/30`
                  : 'bg-white border-zinc-200 hover:border-zinc-400 text-zinc-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon size={18} weight={active ? 'duotone' : 'regular'} />
                <span className="font-semibold text-sm">{opt.label}</span>
              </div>
              <div className="text-[11px] mt-1 opacity-80 leading-snug">{opt.hint}</div>
            </button>
          );
        })}
      </div>

      {/* Type-specific configuration */}
      {t === 'letters' && (
        <div className="rounded-lg bg-zinc-50 border p-3 space-y-2">
          <Label className="text-xs">文字を入力してください（英字）</Label>
          <Input
            data-testid="seg-letters-value"
            value={value.value || ''}
            onChange={(e) => onChange({ ...value, value: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })}
            placeholder="例: SMH"
            maxLength={10}
            className="uppercase"
          />
          <div className="text-[11px] text-zinc-500">
            {value.value ? `→ 「${value.value.toUpperCase()}」（${value.value.length}文字）` : '入力した文字数がそのまま桁数になります'}
          </div>
        </div>
      )}

      {t === 'digits' && (
        <div className="rounded-lg bg-zinc-50 border p-3 space-y-2">
          <Label className="text-xs">数字を入力してください</Label>
          <Input
            data-testid="seg-digits-value"
            value={value.value || ''}
            onChange={(e) => onChange({ ...value, value: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="例: 10"
            maxLength={10}
            inputMode="numeric"
          />
          <div className="text-[11px] text-zinc-500">
            {value.value ? `→ 「${value.value}」（${value.value.length}文字）` : '入力した文字数がそのまま桁数になります'}
          </div>
        </div>
      )}

      {/* iter_144a · The standalone "separator" tile was removed per user
          request. Trailing separators are configured inline on each category
          via the "次との間の区切り文字" block below. */}

      {t === 'serial' && (
        <div className="rounded-lg bg-zinc-50 border p-3 grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">桁数</Label>
            <Input
              data-testid="seg-serial-width"
              type="number"
              min="1"
              max="9"
              value={value.width ?? 5}
              onChange={(e) => onChange({ ...value, width: parseInt(e.target.value, 10) || 5 })}
            />
            <div className="text-[11px] text-zinc-500">1〜9 桁</div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">開始番号</Label>
            <Input
              data-testid="seg-serial-start"
              type="number"
              min="0"
              value={value.start ?? 0}
              onChange={(e) => onChange({ ...value, start: parseInt(e.target.value, 10) || 0 })}
              placeholder="0"
            />
            <div className="text-[11px] text-zinc-500">
              最初のSKU: {previewSegment(value)}
            </div>
          </div>
        </div>
      )}

      {(t === 'year4' || t === 'year2' || t === 'month') && (
        <div className="rounded-lg bg-zinc-50 border p-3 text-xs text-zinc-600">
          発番時に自動で今の {t === 'month' ? '月' : '年'} が入ります。設定は不要です。
          <div className="mt-1 font-mono text-zinc-900">現在の値: {previewSegment(value)}</div>
        </div>
      )}

      {!t && (
        <div className="rounded-lg border-2 border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-500">
          ↑ 上から種別をひとつ選んでください
        </div>
      )}

      {/* iter_144a · Inline trailing separator (per user request: "I use lettering
          ITM but when I choose separator it disappeared the ITM disappeared and
          just be the separator, what I want is ITM- like that").
          - Rendered only when this segment already has a type picked and the
            parent asked for it (showNextSep — hidden on the last segment).
          - Emits ANOTHER segment on save, right after this one, so ITM + "-"
            produces the SKU sample "ITM-…". Round-tripped on edit via
            reverseSegment's separator detection. */}
      {t && showNextSep && t !== 'separator' && (
        <div className="rounded-lg bg-blue-50/40 border border-blue-100 p-3 space-y-2">
          <Label className="text-xs flex items-center gap-1">
            <Divide size={12} weight="duotone" className="text-blue-500" />
            次との間の区切り文字（任意）
          </Label>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onChange({ ...value, nextSep: '' })}
              data-testid="seg-next-sep-none"
              className={`h-8 px-3 rounded-lg border text-xs font-semibold ${
                !value.nextSep
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              なし
            </button>
            {['-', '_', '.', '/', ':'].map((ch) => (
              <button
                key={ch}
                type="button"
                onClick={() => onChange({ ...value, nextSep: ch })}
                data-testid={`seg-next-sep-preset-${ch}`}
                className={`h-8 w-8 rounded-lg border font-mono font-bold ${
                  value.nextSep === ch
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                }`}
              >
                {ch}
              </button>
            ))}
            <Input
              data-testid="seg-next-sep-value"
              value={value.nextSep || ''}
              onChange={(e) => onChange({ ...value, nextSep: (e.target.value || '').slice(0, 1) })}
              maxLength={1}
              placeholder="任意"
              className="w-16 h-8 text-center font-mono"
            />
          </div>
          {value.nextSep && (
            <div className="text-[11px] text-blue-700">
              例: この分類 + 「{value.nextSep}」 + 次の分類 → 「{previewSegment(value)}{value.nextSep}…」
            </div>
          )}
        </div>
      )}
    </div>
  );
}
